import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ArrowIcon from '../../components/ArrowIcon';
import FooterBase from '../../components/Footer';

export function Footer() { return <FooterBase hideCTA />; }

/* ── Reveal hook ── */
export function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { threshold: 0.07 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

/* ── Shared CSS injected once per page via ServiceHero ── */
const SHARED_CSS = `
  @keyframes iotConnHalo{0%,100%{transform:scale(1);opacity:.55}50%{transform:scale(1.85);opacity:1}}
  @keyframes iotConnArrive{0%{transform:scale(.5);opacity:.95}75%,100%{transform:scale(2.8);opacity:0}}
  .iot-conn-head-halo{transform-box:fill-box;transform-origin:center;animation:iotConnHalo 1.05s ease-in-out infinite}
  .iot-conn-arrive-ring{transform-box:fill-box;transform-origin:center;animation:iotConnArrive 1.4s ease-out infinite}
  .iot-proc-step-grid{display:grid;grid-template-columns:1fr 1fr;gap:clamp(36px,5vw,80px);align-items:start}
  @media(max-width:760px){.iot-proc-step-grid{grid-template-columns:1fr;gap:28px}}
  .iot-bento-grid{display:flex;flex-direction:column;gap:14px}
  .iot-bento-row{display:flex;gap:14px}
  .iot-bento-row .iot-bento-card{min-height:190px}
  @media(max-width:560px){.iot-bento-row{flex-direction:column}.iot-bento-row .iot-bento-card{flex:1 1 100%!important}}
  .iot-bento-card{position:relative;overflow:hidden;border-radius:16px;padding:clamp(14px,1.8vw,20px);display:flex;flex-direction:column;cursor:pointer;text-decoration:none}
  .iot-bento-card:hover{box-shadow:0 18px 40px rgba(10,10,20,.16)}
  .iot-bento-icon{transition:transform 220ms cubic-bezier(.22,1,.36,1),opacity 320ms ease;will-change:transform}
  .iot-bento-card:hover .iot-bento-icon{opacity:1}
  .iot-bento-arrow{position:absolute;top:18px;right:18px;width:30px;height:30px;border-radius:999px;display:flex;align-items:center;justify-content:center;background:rgba(255,255,255,.14);opacity:0;transform:translate(-6px,6px) rotate(-45deg);transition:opacity 280ms ease,transform 360ms cubic-bezier(.34,1.56,.64,1)}
  .iot-bento-card:hover .iot-bento-arrow{opacity:1;transform:translate(0,0) rotate(0deg)}
`;

/* ── Stats ── */
export function StatCard({ stat, idx, last, visible }) {
  const target = parseInt(stat.val, 10) || 0;
  const suffix = stat.val.replace(/^[0-9]+/, '');
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!visible) { setCount(0); return; }
    let raf;
    const t0 = performance.now(), dur = 1200;
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / dur);
      setCount(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [visible, target]);
  const accent = idx % 2 === 0 ? 'var(--blue)' : 'var(--red)';
  return (
    <div style={{ textAlign:'center', padding:'clamp(10px,1.8vw,18px) clamp(10px,1.6vw,20px)', borderRight:last?'none':'1px solid rgba(10,10,20,0.08)', opacity:visible?1:0, transform:visible?'translateY(0)':'translateY(16px)', transition:`opacity 460ms ${idx*90}ms, transform 460ms ${idx*90}ms` }}>
      <div style={{ fontFamily:"'Anton',sans-serif", fontWeight:400, fontSize:'clamp(26px,3vw,38px)', color:accent, lineHeight:1, marginBottom:6 }}>{count}{suffix}</div>
      <div style={{ fontSize:10.5, fontWeight:700, color:'var(--ink-soft)', letterSpacing:3, textTransform:'uppercase', fontFamily:"'Inter', sans-serif" }}>{stat.label}</div>
    </div>
  );
}

const STATS_DATA = [
  { val:'8+', label:'Years Experience' },
  { val:'120+', label:'Products Built' },
  { val:'12', label:'Industries Served' },
  { val:'45', label:'Engineers' },
];

export function StatsRow() {
  const [ref, visible] = useReveal();
  return (
    <div style={{ maxWidth:1280, margin:'0 auto', padding:'clamp(20px,3vw,32px) clamp(20px,5vw,48px)' }}>
      <div ref={ref} style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)' }} className="svc-stats-row">
        {STATS_DATA.map((s,i) => <StatCard key={s.label} stat={s} idx={i} last={i===3} visible={visible}/>)}
      </div>
      <style>{`@media(max-width:620px){.svc-stats-row{grid-template-columns:repeat(2,1fr)}}`}</style>
    </div>
  );
}

/* ── Service Hero ── */
export function ServiceHero({ tag, breadcrumb, title, titleAccent, subtitle, geo, color, visible, visual, parentLabel = 'Services', parentHref = '/iot-solutions' }) {
  return (
    <section style={{ background:'transparent', paddingTop:120, paddingBottom:72, position:'relative', overflow:'hidden' }}>
      <style>{SHARED_CSS}</style>
      <div style={{ position:'absolute', inset:0, pointerEvents:'none', backgroundImage:'radial-gradient(rgba(10,10,20,0.032) 1px, transparent 1px)', backgroundSize:'28px 28px' }}/>
      <div style={{ position:'absolute', top:-120, right:'4%', width:600, height:600, borderRadius:'50%', background:`radial-gradient(circle, ${color}12 0%, transparent 60%)`, pointerEvents:'none' }}/>
      <div style={{ position:'absolute', bottom:-80, left:'6%', width:380, height:380, borderRadius:'50%', background:'radial-gradient(circle, rgba(0,60,255,0.06) 0%, transparent 65%)', pointerEvents:'none' }}/>
      <div style={{ maxWidth:1280, margin:'0 auto', padding:'0 clamp(20px,5vw,48px)', position:'relative', zIndex:1 }}>
        <div className="svc-hero-inner" style={{ display:visual?'grid':'block', gridTemplateColumns:'1fr 1.25fr', gap:'clamp(32px,5vw,64px)', alignItems:'center' }}>
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:36, opacity:visible?1:0, transform:visible?'none':'translateY(14px)', transition:'opacity 480ms' }}>
              <Link to="/" style={{ fontSize:11, fontWeight:600, color:'var(--ink-soft)', letterSpacing:'0.04em', fontFamily:"'Inter', sans-serif", textTransform:'uppercase' }}>Home</Link>
              <span style={{ color:'rgba(10,10,20,0.22)', fontSize:11 }}>/</span>
              <Link to={parentHref} style={{ fontSize:11, fontWeight:600, color:'var(--ink-soft)', letterSpacing:'0.04em', fontFamily:"'Inter', sans-serif", textTransform:'uppercase' }}>{parentLabel}</Link>
              <span style={{ color:'rgba(10,10,20,0.22)', fontSize:11 }}>/</span>
              <span style={{ fontSize:11, fontWeight:600, color:'var(--ink)', letterSpacing:'0.04em', fontFamily:"'Inter', sans-serif", textTransform:'uppercase' }}>{breadcrumb}</span>
            </div>
            <div style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'5px 14px', background:`${color}0F`, border:`1px solid ${color}2E`, borderRadius:999, marginBottom:22, opacity:visible?1:0, transform:visible?'none':'translateY(16px)', transition:'opacity 480ms 60ms' }}>
              <span style={{ width:5, height:5, borderRadius:'50%', background:color, flexShrink:0 }}/>
              <span style={{ fontSize:10.5, fontWeight:700, letterSpacing:3, textTransform:'uppercase', color:color, fontFamily:"'Inter', sans-serif" }}>{tag}</span>
            </div>
            <h1 style={{ fontFamily:"'Anton',sans-serif", fontWeight:400, fontSize:'clamp(34px,5.5vw,68px)', lineHeight:1.05, color:'var(--ink)', marginBottom:20, maxWidth:820, opacity:visible?1:0, transform:visible?'none':'translateY(22px)', transition:'opacity 520ms 120ms' }}>
              {title}{' '}
              <span style={{ fontFamily:"'Instrument Serif',serif", fontStyle:'italic', fontWeight:400, color:color }}>{titleAccent}</span>
            </h1>
            <p style={{ fontSize:'clamp(15px,1.5vw,17px)', lineHeight:1.82, color:'var(--ink-soft)', maxWidth:560, marginBottom:12, opacity:visible?1:0, transform:visible?'none':'translateY(18px)', transition:'opacity 520ms 180ms' }}>{subtitle}</p>
            <p style={{ fontSize:12.5, lineHeight:1.7, color:'rgba(10,10,20,0.36)', maxWidth:460, marginBottom:36, fontFamily:"'Inter', sans-serif", opacity:visible?1:0, transition:'opacity 520ms 230ms' }}>{geo}</p>
            <div style={{ display:'flex', flexWrap:'wrap', gap:12, opacity:visible?1:0, transition:'opacity 520ms 260ms' }}>
              <Link to="/contact" style={{ display:'inline-flex', alignItems:'center', gap:10, padding:'13px 26px', background:`linear-gradient(135deg,${color},${color}CC)`, color:'white', borderRadius:999, fontSize:13, fontWeight:700,  boxShadow:`0 8px 24px ${color}44` }}>
                Discuss This Service <ArrowIcon size={12}/>
              </Link>
              <Link to={parentHref} style={{ display:'inline-flex', alignItems:'center', gap:10, padding:'13px 26px', background:'rgba(255,255,255,0.82)', border:'1.5px solid rgba(10,10,20,0.12)', color:'var(--ink)', borderRadius:999, fontSize:13, fontWeight:600,  }}>
                All {parentLabel} <ArrowIcon size={12}/>
              </Link>
            </div>
          </div>
          {visual && (
            <div className="svc-hero-visual" style={{ display:'flex', justifyContent:'center', opacity:visible?1:0, transform:visible?'none':'translateY(24px) scale(0.96)', transition:'opacity 700ms 300ms, transform 700ms 300ms' }}>
              {visual}
            </div>
          )}
        </div>
        <style>{`@media(max-width:820px){.svc-hero-inner{display:block!important}.svc-hero-visual{display:none!important}}`}</style>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════
   PROCESS SECTION — identical mechanics to IoTSolutionsPage
   Accepts steps: [{num, title, desc, points}] (3 items)
══════════════════════════════════════════════════════ */
const PROCESS_VISUALS = ['/visuals/process/1.png', '/visuals/process/2.png', '/visuals/process/3.png'];

function getVisualEdgeAnchors(fromEl, toEl, wrapEl) {
  if (!fromEl || !toEl || !wrapEl) return null;
  const wrap = wrapEl.getBoundingClientRect();
  const edgePoint = (el, atBottom) => {
    const r = el.getBoundingClientRect();
    if (!r || (r.width === 0 && r.height === 0)) return null;
    return { x: r.left + r.width / 2 - wrap.left, y: (atBottom ? r.bottom : r.top) - wrap.top };
  };
  const from = edgePoint(fromEl, true);
  const to   = edgePoint(toEl,   false);
  if (!from || !to) return null;
  return { x1: from.x, y1: from.y, x2: to.x, y2: to.y };
}

function ConnectorLine({ onProgress, enabled = true, fromRef, toRef, color }) {
  const wrapRef     = useRef(null);
  const pathRef     = useRef(null);
  const lastAnchors = useRef(null);
  const [pathLen,  setPathLen]  = useState(0);
  const [progress, setProgress] = useState(0);
  const [anchors,  setAnchors]  = useState(null);
  const [tip,      setTip]      = useState(null);
  const H = 110;

  const measure = () => {
    if (!wrapRef.current || !fromRef?.current || !toRef?.current) return;
    const next = getVisualEdgeAnchors(fromRef.current, toRef.current, wrapRef.current);
    if (!next) return;
    const prev = lastAnchors.current;
    if (!prev || Math.abs(prev.x1-next.x1)>.5 || Math.abs(prev.y1-next.y1)>.5 ||
        Math.abs(prev.x2-next.x2)>.5 || Math.abs(prev.y2-next.y2)>.5) {
      lastAnchors.current = next;
      setAnchors(next);
    }
  };

  useEffect(() => {
    measure();
    const ro = new ResizeObserver(measure);
    if (wrapRef.current)  ro.observe(wrapRef.current);
    if (fromRef?.current) ro.observe(fromRef.current);
    if (toRef?.current)   ro.observe(toRef.current);
    window.addEventListener('resize', measure);
    const ticks = [80,160,260,380,520,700,900];
    const timers = ticks.map(ms => setTimeout(measure, ms));
    return () => { ro.disconnect(); window.removeEventListener('resize', measure); timers.forEach(clearTimeout); };
  }, []);

  useEffect(() => {
    if (pathRef.current && anchors) {
      try { setPathLen(pathRef.current.getTotalLength()); } catch(_) {}
    }
  }, [anchors]);

  useEffect(() => {
    const onScroll = () => {
      if (!wrapRef.current) return;
      measure();
      if (!enabled) { setProgress(0); if (onProgress) onProgress(0); return; }
      const a = lastAnchors.current;
      if (!a) return;
      const wrapTop = wrapRef.current.getBoundingClientRect().top;
      const destY   = wrapTop + a.y2;
      const vh      = window.innerHeight;
      const prog = Math.max(0, Math.min(1, (vh - destY) / (vh * 0.5)));
      setProgress(prog);
      if (onProgress) onProgress(prog);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [onProgress, enabled]);

  useEffect(() => {
    if (!pathRef.current || !pathLen || progress <= 0.005 || progress >= 0.992) { setTip(null); return; }
    try {
      const pt = pathRef.current.getPointAtLength(pathLen * progress);
      setTip({ x: pt.x, y: pt.y });
    } catch(_) { setTip(null); }
  }, [progress, pathLen]);

  if (!anchors) return <div ref={wrapRef} style={{ width:'100%', height:H }}/>;
  const { x1, y1, x2, y2 } = anchors;
  const midY = (y1 + y2) / 2;
  const d = `M ${x1} ${y1} C ${x1} ${midY}, ${x2} ${midY}, ${x2} ${y2}`;
  const arriveT = Math.max(0, Math.min(1, (progress - 0.78) / 0.18));

  return (
    <div ref={wrapRef} style={{ width:'100%', height:H, position:'relative', zIndex:2 }}>
      <svg width="100%" height={H} style={{ position:'absolute', left:0, top:0, overflow:'visible' }}>
        <path ref={pathRef} d={d} stroke={color} strokeWidth="2.5" fill="none"
          strokeLinecap="round" strokeLinejoin="round"
          strokeDasharray={pathLen || 9999} strokeDashoffset={pathLen ? pathLen*(1-progress) : 9999}/>
        {tip && (
          <g style={{ opacity: Math.min(1, progress*14) }}>
            <circle cx={tip.x} cy={tip.y} r="6.5" fill={color} fillOpacity="0.18" className="iot-conn-head-halo"/>
            <circle cx={tip.x} cy={tip.y} r="3.2" fill={color}/>
            <circle cx={tip.x} cy={tip.y} r="1.3" fill="#fff"/>
          </g>
        )}
        {arriveT > 0 && (
          <g style={{ opacity: arriveT }}>
            <circle cx={x2} cy={y2} r="2.6" fill={color}/>
            <circle cx={x2} cy={y2} r="6" fill="none" stroke={color} strokeWidth="1.4" className="iot-conn-arrive-ring"/>
            <circle cx={x2} cy={y2} r="6" fill="none" stroke={color} strokeWidth="1.4" className="iot-conn-arrive-ring" style={{ animationDelay:'0.55s' }}/>
          </g>
        )}
      </svg>
    </div>
  );
}

function ProcessStepBlock({ step, stepIdx, stepCount, imageLeft, revealed, dimmed = false, visualRef, color }) {
  const emphasis = !revealed ? 0 : (dimmed ? 0.55 : 1);
  const illustration = (
    <div style={{ display:'block', lineHeight:0, opacity:emphasis, transform:!revealed?'scale(0.84) translateY(36px)':(dimmed?'scale(0.94) translateY(0)':'scale(1) translateY(0)'), transition:'opacity 520ms cubic-bezier(.2,.8,.2,1), transform 520ms cubic-bezier(.2,.8,.2,1)' }}>
      <img ref={visualRef} src={PROCESS_VISUALS[stepIdx]} alt={step.title} style={{ width:'100%', maxWidth:460, display:'block', borderRadius:20 }}/>
    </div>
  );
  const content = (
    <div style={{ display:'flex', flexDirection:'column', justifyContent:'center', opacity:emphasis, transform:!revealed?`translateX(${imageLeft?'32px':'-32px'})`:'translateX(0)', transition:'opacity 480ms cubic-bezier(.2,.8,.2,1) 80ms, transform 480ms cubic-bezier(.2,.8,.2,1) 80ms' }}>
      <div style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'4px 12px', background:`${color}12`, border:`1px solid ${color}2E`, borderRadius:999, marginBottom:18, width:'fit-content' }}>
        <span style={{ width:4, height:4, borderRadius:'50%', background:color, flexShrink:0 }}/>
        <span style={{ fontFamily:"'Inter', sans-serif", fontWeight:700, fontSize:9.5, letterSpacing:3, color:color, textTransform:'uppercase' }}>
          STEP {step.num} OF {stepCount}
        </span>
      </div>
      <h3 style={{ fontFamily:"'Anton',sans-serif", fontWeight:400, fontSize:'clamp(26px,3.5vw,42px)', lineHeight:1.08, color:'var(--ink)', marginBottom:16 }}>{step.title}</h3>
      <p style={{ fontSize:15.5, lineHeight:1.85, color:'var(--ink-soft)', marginBottom:28, maxWidth:440 }}>{step.desc}</p>
      <div style={{ width:36, height:2, background:color, borderRadius:2, marginBottom:24, opacity:0.6 }}/>
      <ul style={{ listStyle:'none', padding:0, margin:0 }}>
        {step.points.map((pt, j) => (
          <li key={j} style={{ display:'flex', alignItems:'flex-start', gap:13, paddingTop:j>0?13:0, paddingBottom:j<step.points.length-1?13:0, borderTop:j>0?'1px solid rgba(10,10,20,0.06)':'none', opacity:revealed?1:0, transform:revealed?'translateY(0)':'translateY(14px)', transition:`opacity 460ms ease ${j*80+320}ms, transform 460ms ease ${j*80+320}ms` }}>
            <div style={{ width:18, height:18, borderRadius:5, background:`${color}14`, border:`1px solid ${color}33`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:2 }}>
              <svg width="9" height="9" viewBox="0 0 10 10" fill="none"><path d="M1.5 5l2.5 2.5 4.5-4.5" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>
            <span style={{ fontSize:14.5, lineHeight:1.65, color:'var(--ink-soft)', fontWeight:500 }}>{pt}</span>
          </li>
        ))}
      </ul>
    </div>
  );
  return (
    <div className="iot-proc-step-grid" style={{ minHeight:'clamp(280px,40vh,400px)', alignItems:'flex-start', paddingTop:'clamp(20px,3vh,36px)' }}>
      {imageLeft ? <>{illustration}{content}</> : <>{content}{illustration}</>}
    </div>
  );
}

export function ProcessSection({ steps, color }) {
  const [headRef, headVisible] = useReveal();
  const vr0 = useRef(null), vr1 = useRef(null), vr2 = useRef(null);
  const vr3 = useRef(null), vr4 = useRef(null);
  const vr = [vr0, vr1, vr2, vr3, vr4];
  const step1Ref = useRef(null);
  const [step1Visible, setStep1Visible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => setStep1Visible(e.isIntersecting), { threshold: 0.08 });
    if (step1Ref.current) obs.observe(step1Ref.current);
    return () => obs.disconnect();
  }, []);
  const [c0, setC0] = useState(0);
  const [c1, setC1] = useState(0);
  const [c2, setC2] = useState(0);
  const [c3, setC3] = useState(0);
  const cSets = [setC0, setC1, setC2, setC3];
  const n = steps.length;
  const stepVisible = [step1Visible, c0>0.92, c1>0.92, c2>0.92, c3>0.92];
  const stepDimmed  = [c0>0.5, c1>0.5, c2>0.5, c3>0.5, false];

  return (
    <section style={{ background:'transparent' }}>
      <div ref={headRef} style={{ maxWidth:1280, margin:'0 auto', padding:'clamp(56px,7vw,92px) clamp(20px,5vw,48px) clamp(40px,5vw,60px)', opacity:headVisible?1:0, transform:headVisible?'translateY(0)':'translateY(22px)', transition:'opacity 520ms, transform 520ms' }}>
        <span style={{ fontFamily:"'Inter', sans-serif", fontWeight:700, fontSize:10.5, letterSpacing:3, color:color, textTransform:'uppercase' }}>HOW WE WORK</span>
        <h2 style={{ fontFamily:"'Anton',sans-serif", fontWeight:400, fontSize:'clamp(28px,4vw,48px)', lineHeight:1.07, marginTop:8, marginBottom:10 }}>
          Our{' '}<span style={{ fontFamily:"'Instrument Serif',serif", fontStyle:'italic', fontWeight:400, color:color }}>Process</span>
        </h2>
        <p style={{ fontSize:15, color:'var(--ink-soft)', maxWidth:500, lineHeight:1.78 }}>
          From your first idea to a live, production-ready product — here is how we get there together.
        </p>
      </div>
      <div style={{ maxWidth:1280, margin:'0 auto', padding:'0 clamp(20px,5vw,48px) clamp(28px,4vw,48px)' }}>
        {steps.map((step, i) => {
          const block = (
            <ProcessStepBlock
              step={step} stepIdx={i % 3} stepCount={n}
              imageLeft={i % 2 === 0} revealed={stepVisible[i]}
              dimmed={stepDimmed[i]} visualRef={vr[i]} color={color}
            />
          );
          return (
            <React.Fragment key={i}>
              {i === 0 ? <div ref={step1Ref}>{block}</div> : block}
              {i < n - 1 && (
                <ConnectorLine
                  onProgress={cSets[i]}
                  enabled={i === 0 || stepVisible[i]}
                  fromRef={vr[i]} toRef={vr[i + 1]}
                  color={color}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════
   INDUSTRIES BENTO — identical to IoTSolutionsPage
   Cards are Links to their industry pages. Kept in sync with the 4 sectors
   in src/data/sectorServicesData.js — Zone-X currently serves exactly these.
══════════════════════════════════════════════════════ */
/* Each tile gets its own `weight` (flex-grow share within its row), so all
   4 tiles render at genuinely different widths — row 1 splits 62/38, row 2
   splits 40/60, giving four distinct sizes instead of two repeated ones. */
const INDUSTRIES = [
  { name:'Healthcare',                    desc:'Patient monitoring, smart diagnostics, connected medical devices', weight:62, bg:'#DDF5E7', fg:'#15151F', icon:'#3FAE74', to:'/industries/healthcare' },
  { name:'Utilities & Energy',            desc:'Smart metering, grid monitoring, energy management systems',       weight:38, bg:'#FCE6D9', fg:'#15151F', icon:'#E0793C', to:'/industries/utilities-energy' },
  { name:'Industrial IoT & Automation',   desc:'Factory automation, machine monitoring, process control',          weight:40, bg:'#EAE4FB', fg:'#15151F', icon:'#7C5FD0', to:'/industries/industrial-iot' },
  { name:'Education',                     desc:'STEM kits, robotics labs, cloud-based learning platforms',         weight:60, bg:'#F4E3FB', fg:'#15151F', icon:'#A85AD0', to:'/industries/education' },
];
const INDUSTRY_ROWS = [INDUSTRIES.slice(0, 2), INDUSTRIES.slice(2, 4)];

function IndustryIcon({ name, color, size }) {
  const c = color; const s = size || 26;
  if (name==='Healthcare')                  return <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke={c} strokeWidth="1.5"/><path d="M12 8v8M8 12h8" stroke={c} strokeWidth="2" strokeLinecap="round"/></svg>;
  if (name==='Utilities & Energy')          return <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke={c} strokeWidth="1.5" strokeLinejoin="round"/></svg>;
  if (name==='Industrial IoT & Automation') return <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="3" stroke={c} strokeWidth="1.5"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3M5.64 5.64l2.12 2.12M16.24 16.24l2.12 2.12M5.64 18.36l2.12-2.12M16.24 7.76l2.12-2.12" stroke={c} strokeWidth="1.5" strokeLinecap="round"/></svg>;
  if (name==='Education')                   return <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M12 3L2 8l10 5 10-5-10-5z" stroke={c} strokeWidth="1.5" strokeLinejoin="round"/><path d="M6 10.6V16c0 1.66 2.69 3 6 3s6-1.34 6-3v-5.4" stroke={c} strokeWidth="1.5" strokeLinecap="round"/></svg>;
  return <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><rect x="7" y="2" width="10" height="20" rx="2.5" stroke={c} strokeWidth="1.5"/><circle cx="12" cy="18" r="1.5" fill={c}/><path d="M9 6h6" stroke={c} strokeWidth="1.5" strokeLinecap="round"/></svg>;
}

export function IndustriesSection() {
  const [indRef, indVisible] = useReveal();
  const bentoIconRefs = useRef([]);

  const handleBentoMove = (e, i) => {
    const el = bentoIconRefs.current[i];
    if (!el) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width  - 0.5;
    const py = (e.clientY - rect.top)  / rect.height - 0.5;
    el.style.transform = `translate(${(-px*34).toFixed(1)}px, ${(-py*34).toFixed(1)}px) scale(1.12)`;
  };
  const handleBentoLeave = (i) => {
    const el = bentoIconRefs.current[i];
    if (el) el.style.transform = 'translate(0px,0px) scale(1)';
  };

  return (
    <section style={{ background:'transparent', padding:'clamp(56px,7vw,92px) 0' }}>
      <div style={{ maxWidth:1280, margin:'0 auto', padding:'0 clamp(20px,5vw,48px)' }}>
        <div ref={indRef}>
          <div style={{ marginBottom:44, opacity:indVisible?1:0, transform:indVisible?'none':'translateY(18px)', transition:'opacity 480ms, transform 480ms' }}>
            <span style={{ fontFamily:"'Inter', sans-serif", fontWeight:700, fontSize:10.5, letterSpacing:3, color:'var(--blue)', textTransform:'uppercase' }}>INDUSTRIES</span>
            <h2 style={{ fontFamily:"'Anton',sans-serif", fontWeight:400, fontSize:'clamp(28px,4vw,48px)', lineHeight:1.07, marginTop:8, marginBottom:10 }}>
              Industries We{' '}<span style={{ fontFamily:"'Instrument Serif',serif", fontStyle:'italic', fontWeight:400, color:'var(--red)' }}>Serve</span>
            </h2>
            <p style={{ fontSize:15, color:'var(--ink-soft)', maxWidth:580, lineHeight:1.78 }}>
              Zone-X delivers IoT solutions across four industries — healthcare, utilities &amp; energy, industrial IoT &amp; automation, and education.
            </p>
          </div>
          <div className="iot-bento-grid">
            {INDUSTRY_ROWS.map((row, ri) => (
              <div className="iot-bento-row" key={ri}>
                {row.map((ind, ci) => {
                  const i = ri * 2 + ci;
                  const big = ind.weight >= 50;
                  return (
                    <Link
                      key={ind.name}
                      to={ind.to}
                      className="iot-bento-card hoverable"
                      onMouseMove={(e) => handleBentoMove(e, i)}
                      onMouseLeave={() => handleBentoLeave(i)}
                      style={{
                        flex: `${ind.weight} 1 0`,
                        background: ind.bg,
                        color: ind.fg,
                        justifyContent: 'space-between',
                        opacity: indVisible ? 1 : 0,
                        transform: indVisible ? 'none' : 'translateY(18px)',
                        transitionProperty: 'opacity, transform, box-shadow',
                        transitionDuration: '520ms, 520ms, 360ms',
                        transitionDelay: `${i*60}ms, ${i*60}ms, 0ms`,
                      }}
                    >
                      <div className="iot-bento-arrow" style={{ color: ind.fg }}>
                        <ArrowIcon size={13}/>
                      </div>
                      <div
                        ref={(el) => { bentoIconRefs.current[i] = el; }}
                        className="iot-bento-icon"
                        style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', margin:'4px 0', opacity:0.7, transform:'translate(0px,0px) scale(1)' }}
                      >
                        <IndustryIcon name={ind.name} color={ind.icon} size={big ? 64 : 40}/>
                      </div>
                      <div>
                        <div style={{ fontSize:big ? 19 : 13, fontWeight:800, lineHeight:1.28 }}>{ind.name}</div>
                        <div style={{ fontSize:big ? 13 : 11, fontWeight:500, lineHeight:1.4, opacity:0.7, marginTop:4, maxWidth:big ? 260 : 'none' }}>{ind.desc}</div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Why Zone-X — entire section commented out ──
export function WhyZoneX() {
  const [ref, visible] = useReveal();
  return (
    <section style={{ background:'transparent', padding:'clamp(64px,8vw,104px) 0' }}>
      <div ref={ref} style={{ maxWidth:1280, margin:'0 auto', padding:'0 clamp(20px,5vw,48px)' }}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'clamp(40px,6vw,88px)', alignItems:'start' }} className="why-grid">
          <div style={{ opacity:visible?1:0, transform:visible?'none':'translateY(24px)', transition:'opacity 520ms, transform 520ms' }}>
            <span style={{ fontFamily:"'Anton', sans-serif", fontSize:10.5, color:'var(--red)', textTransform:'uppercase' }}>WHY ZONE-X</span>
            <h2 style={{ fontFamily:"'Anton',sans-serif", fontSize:'clamp(30px,4.5vw,56px)', lineHeight:1.05, color:'var(--ink)', marginTop:10, marginBottom:22 }}>
              One team.<br/><span style={{ fontFamily:"'Instrument Serif',serif", fontStyle:'italic', fontWeight:400, color:'var(--red)' }}>Every layer.</span>
            </h2>
            <p style={{ fontSize:15.5, lineHeight:1.88, color:'var(--ink-soft)', marginBottom:20 }}>
              Businesses choose Zone-X because we combine IoT, hardware, software, embedded systems, and product R&D in one place. Fewer handoffs. Better communication. A smoother product journey.
            </p>
            <p style={{ fontSize:15.5, lineHeight:1.88, color:'var(--ink-soft)' }}>
              Whether you need IoT product engineering in Bangalore, custom IoT design in Karnataka, or a long-term embedded systems partner — Zone-X is ready.
            </p>
          </div>
          <div style={{ opacity:visible?1:0, transform:visible?'none':'translateY(24px)', transition:'opacity 520ms 100ms, transform 520ms 100ms' }}>
            {[
              { num:'01', title:'End-to-end under one roof',   desc:'Hardware, firmware, embedded software, cloud, and mobile — all handled internally without third-party handoffs.' },
              { num:'02', title:'Deep technical expertise',     desc:'Engineers with real hands-on experience across embedded systems, IoT protocols, PCB design, and cloud architecture.' },
              { num:'03', title:'Business-aligned engineering', desc:'We focus on solving real business problems, not just building technically impressive systems that miss the goal.' },
              { num:'04', title:'Transparent collaboration',    desc:'Regular updates, clear timelines, and open communication throughout every phase of your product.' },
            ].map((item, i) => (
              <div key={item.num} style={{ display:'flex', gap:20, alignItems:'flex-start', paddingTop:i>0?24:0, paddingBottom:i<3?24:0, borderBottom:i<3?'1px solid rgba(10,10,20,0.08)':'none', opacity:visible?1:0, transform:visible?'none':'translateY(16px)', transition:`opacity 500ms ${i*80+160}ms, transform 500ms ${i*80+160}ms` }}>
                <span style={{ fontFamily:"'Anton', sans-serif", fontSize:10, color:i%2===0?'var(--blue)':'var(--red)', flexShrink:0, paddingTop:3 }}>{item.num}</span>
                <div>
                  <div style={{ fontSize:15, fontWeight:700, color:'var(--ink)', marginBottom:6, lineHeight:1.3 }}>{item.title}</div>
                  <div style={{ fontSize:14, lineHeight:1.72, color:'var(--ink-soft)' }}>{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <style>{`@media(max-width:820px){.why-grid{grid-template-columns:1fr!important}}`}</style>
    </section>
  );
}
*/
export function WhyZoneX() { return null; }

/* ── Service CTA ── */
export function ServiceCTA() {
  const [ref, visible] = useReveal();
  return (
    <section style={{ padding:'clamp(56px,7vw,88px) 0 clamp(24px,3vw,40px)' }}>
      <div style={{ maxWidth:1280, margin:'0 auto', padding:'0 clamp(20px,5vw,48px)' }}>
        <div ref={ref} style={{ background:'linear-gradient(135deg, var(--ink) 0%, #1a1a2e 100%)', borderRadius:28, padding:'clamp(48px,6vw,88px) clamp(32px,5vw,80px)', textAlign:'center', position:'relative', overflow:'hidden', opacity:visible?1:0, transform:visible?'none':'translateY(24px)', transition:'opacity 520ms, transform 520ms' }}>
          <div aria-hidden style={{ position:'absolute', inset:0, opacity:0.5, background:'radial-gradient(ellipse at 25% 50%, rgba(255,0,4,0.28), transparent 55%), radial-gradient(ellipse at 78% 50%, rgba(0,60,255,0.18), transparent 55%)' }}/>
          <div style={{ position:'relative' }}>
            <span style={{ fontFamily:"'Inter', sans-serif", fontWeight:700, fontSize:10.5, letterSpacing:3, color:'rgba(255,255,255,0.35)', textTransform:'uppercase' }}>START YOUR PROJECT</span>
            <p style={{ fontFamily:"'Anton', sans-serif", fontWeight:400, fontSize:'clamp(28px,4.5vw,58px)', color:'white', lineHeight:1.2, margin:'14px 0 16px', }}>
              Ready to build your <span style={{ color:'#ff4d4d' }}>next product?</span>
            </p>
            <p style={{ fontSize:16, color:'rgba(255,255,255,0.5)', maxWidth:440, margin:'0 auto 40px' }}>
              Tell us about your idea. Our team will evaluate your requirements and get back to you quickly.
            </p>
            <Link to="/contact" className="hoverable" style={{ display:'inline-flex', alignItems:'center', gap:12, padding:'17px 40px', background:'linear-gradient(135deg, var(--red), #cc0003)', color:'white', borderRadius:999, fontSize:14, fontWeight:700,  boxShadow:'0 18px 48px rgba(255,0,4,0.38)', transition:'transform 200ms ease, box-shadow 200ms ease' }}
              onMouseEnter={e => { e.currentTarget.style.transform='translateY(-3px)'; e.currentTarget.style.boxShadow='0 24px 56px rgba(255,0,4,0.5)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='0 18px 48px rgba(255,0,4,0.38)'; }}>
              Get In Touch <ArrowIcon size={13}/>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Case Studies ── */
export function ServiceCaseStudies({ studies, color }) {
  const [ref, visible] = useReveal();
  const [active, setActive] = useState(0);
  const s = studies[active];

  return (
    <section style={{ background:'transparent', padding:'clamp(48px,6vw,80px) 0' }}>
      <style>{`
        .cs-selector-btn { transition: background 200ms, border-color 200ms; }
        .cs-selector-btn:hover { background: ${color}07 !important; }
        .cs-detail-panel { transition: opacity 220ms ease; }
        @media(max-width:820px){ .cs-layout{ grid-template-columns:1fr!important } .cs-selectors{ flex-direction:row!important; overflow-x:auto; } .cs-selector-btn{ min-width:200px; border-left:none!important; border-bottom:3px solid transparent; } }
      `}</style>

      <div style={{ maxWidth:1280, margin:'0 auto', padding:'0 clamp(20px,5vw,48px)' }}>

        {/* Header row */}
        <div ref={ref} style={{ display:'flex', alignItems:'flex-end', justifyContent:'space-between', flexWrap:'wrap', gap:16, marginBottom:44, opacity:visible?1:0, transform:visible?'none':'translateY(18px)', transition:'opacity 480ms, transform 480ms' }}>
          <div>
            <span style={{ fontFamily:"'Inter', sans-serif", fontWeight:700, fontSize:10.5, letterSpacing:3, color, textTransform:'uppercase' }}>CASE STUDIES</span>
            <h2 style={{ fontFamily:"'Anton',sans-serif", fontWeight:400, fontSize:'clamp(28px,4vw,48px)', lineHeight:1.07, marginTop:8, marginBottom:0 }}>
              Work That{' '}<span style={{ fontFamily:"'Instrument Serif',serif", fontStyle:'italic', fontWeight:400, color }}>Speaks</span>
            </h2>
          </div>
          <div style={{ fontFamily:"'Anton', sans-serif", fontWeight:400, fontSize:11, color:'var(--ink-soft)', paddingBottom:6 }}>
            {String(active+1).padStart(2,'0')} / {String(studies.length).padStart(2,'0')}
          </div>
        </div>

        {/* Main layout */}
        <div className="cs-layout" style={{ display:'grid', gridTemplateColumns:'340px 1fr', gap:'clamp(12px,1.6vw,18px)', opacity:visible?1:0, transform:visible?'none':'translateY(22px)', transition:'opacity 560ms 80ms, transform 560ms 80ms' }}>

          {/* Selector column */}
          <div className="cs-selectors" style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {studies.map((st, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className="cs-selector-btn"
                style={{
                  display:'flex', flexDirection:'column', gap:8,
                  padding:'clamp(16px,2vw,22px) clamp(18px,2.2vw,24px)',
                  background: active===i ? `${color}0C` : 'rgba(255,255,255,0.72)',
                  border:`1px solid ${active===i ? color+'2E' : 'rgba(10,10,20,0.08)'}`,
                  borderLeft:`3px solid ${active===i ? color : 'transparent'}`,
                  borderRadius:14, cursor:'pointer', textAlign:'left',
                }}
              >
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ fontFamily:"'Anton', sans-serif", fontWeight:400, fontSize:9, letterSpacing:'0.04em', color: active===i ? color : 'rgba(10,10,20,0.3)' }}>
                    {String(i+1).padStart(2,'0')}
                  </span>
                  <span style={{ width:1, height:9, background:'rgba(10,10,20,0.12)', flexShrink:0 }}/>
                  <span style={{ fontFamily:"'Inter', sans-serif", fontWeight:700, fontSize:9, letterSpacing:2, textTransform:'uppercase', color: active===i ? color : 'var(--ink-soft)' }}>
                    {st.tag}
                  </span>
                </div>
                <div style={{ fontFamily:"'Anton',sans-serif", fontWeight:400, fontSize:'clamp(14px,1.6vw,18px)', lineHeight:1.18, color: active===i ? 'var(--ink)' : 'var(--ink-soft)' }}>
                  {st.title}
                </div>
                {active===i && (
                  <div style={{ fontFamily:"'Anton',sans-serif", fontWeight:400, fontSize:'clamp(18px,2.2vw,24px)', color, lineHeight:1 }}>
                    {st.metric}
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* Feature panel */}
          <div style={{ position:'relative', background:'rgba(255,255,255,0.86)', border:'1px solid rgba(10,10,20,0.08)', borderRadius:20, overflow:'hidden', padding:'clamp(32px,4vw,56px)' }}>

            {/* Ghost watermark metric */}
            <div aria-hidden style={{
              position:'absolute', bottom:-18, right:-12, lineHeight:1,
              fontFamily:"'Anton',sans-serif",
              fontWeight:400,
              fontSize:'clamp(88px,12vw,152px)',
              color:`${color}07`,
              userSelect:'none', pointerEvents:'none',
              whiteSpace:'nowrap',
            }}>{s.metric}</div>

            {/* Accent corner dot */}
            <div style={{ position:'absolute', top:0, right:0, width:160, height:160, background:`radial-gradient(circle at top right, ${color}10 0%, transparent 70%)`, pointerEvents:'none' }}/>

            {/* Content */}
            <div style={{ position:'relative', display:'flex', flexDirection:'column', gap:0 }}>

              {/* Tag */}
              <div style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'4px 12px', background:`${color}0F`, border:`1px solid ${color}25`, borderRadius:999, width:'fit-content', marginBottom:20 }}>
                <div style={{ width:5, height:5, borderRadius:'50%', background:color, flexShrink:0 }}/>
                <span style={{ fontFamily:"'Inter', sans-serif", fontWeight:700, fontSize:9.5, letterSpacing:2.5, color, textTransform:'uppercase' }}>{s.tag}</span>
              </div>

              {/* Big metric */}
              <div style={{ fontFamily:"'Anton',sans-serif", fontWeight:400, fontSize:'clamp(42px,6vw,76px)', lineHeight:0.95, color, marginBottom:16 }}>
                {s.metric}
              </div>

              {/* Title */}
              <h3 style={{ fontFamily:"'Anton',sans-serif", fontWeight:400, fontSize:'clamp(22px,3vw,36px)', lineHeight:1.08, color:'var(--ink)', margin:'0 0 20px' }}>
                {s.title}
              </h3>

              {/* Rule */}
              <div style={{ width:48, height:2, background:color, borderRadius:2, marginBottom:20, opacity:0.7 }}/>

              {/* Description */}
              <p style={{ fontSize:'clamp(14px,1.3vw,15.5px)', lineHeight:1.82, color:'var(--ink-soft)', margin:0, maxWidth:560 }}>
                {s.desc}
              </p>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}

/* ── FAQ ── */
export function ServiceFAQ({ faqs, color }) {
  const [ref, visible] = useReveal();
  const [open, setOpen] = useState(null);
  return (
    <section style={{ padding:'clamp(48px,6vw,80px) 0' }}>
      <div style={{ maxWidth:1280, margin:'0 auto', padding:'0 clamp(20px,5vw,48px)' }}>
        <div ref={ref} style={{ marginBottom:40, opacity:visible?1:0, transform:visible?'none':'translateY(18px)', transition:'opacity 480ms, transform 480ms' }}>
          <span style={{ fontFamily:"'Inter', sans-serif", fontWeight:700, fontSize:10.5, letterSpacing:3, color, textTransform:'uppercase' }}>FAQ</span>
          <h2 style={{ fontFamily:"'Anton',sans-serif", fontWeight:400, fontSize:'clamp(28px,4vw,48px)', lineHeight:1.07, marginTop:8 }}>
            Frequently Asked{' '}<span style={{ fontFamily:"'Instrument Serif',serif", fontStyle:'italic', fontWeight:400, color }}>Questions</span>
          </h2>
        </div>
        <div style={{ maxWidth:880, display:'flex', flexDirection:'column', gap:10 }}>
          {faqs.map((faq, i) => (
            <div key={i} style={{ background:'rgba(255,255,255,0.78)', border:`1px solid ${open===i ? color+'33' : 'rgba(10,10,20,0.08)'}`, borderRadius:16, overflow:'hidden', opacity:visible?1:0, transform:visible?'none':'translateY(18px)', transition:`opacity 450ms ${i*45+80}ms, transform 450ms ${i*45+80}ms, border-color 220ms` }}>
              <button
                onClick={() => setOpen(open===i ? null : i)}
                style={{ width:'100%', display:'flex', justifyContent:'space-between', alignItems:'center', gap:20, padding:'clamp(16px,2vw,22px) clamp(18px,2.2vw,26px)', background:'none', border:'none', cursor:'pointer', textAlign:'left' }}
              >
                <span style={{ fontSize:15, fontWeight:700, color:'var(--ink)', lineHeight:1.4, flex:1 }}>{faq.q}</span>
                <span style={{ width:28, height:28, borderRadius:'50%', background:open===i ? color+'18' : 'transparent', border:`1px solid ${open===i ? color+'44' : 'rgba(10,10,20,0.12)'}`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, transition:'background 220ms, border-color 220ms' }}>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ transform:open===i?'rotate(45deg)':'none', transition:'transform 240ms cubic-bezier(.34,1.56,.64,1)' }}>
                    <path d="M6 1v10M1 6h10" stroke={open===i ? color : 'var(--ink)'} strokeWidth="1.8" strokeLinecap="round"/>
                  </svg>
                </span>
              </button>
              <div style={{ maxHeight:open===i ? 500 : 0, overflow:'hidden', transition:'max-height 320ms cubic-bezier(0.4,0,0.2,1)' }}>
                <div style={{ borderTop:`1px solid ${color}18`, padding:`14px clamp(18px,2.2vw,26px) clamp(16px,2vw,22px)` }}>
                  <p style={{ fontSize:14.5, lineHeight:1.82, color:'var(--ink-soft)', margin:0 }}>{faq.a}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── What We Deliver — entire section commented out ──
export function WhatWeDeliver({ items, color }) {
  const [ref, visible] = useReveal();
  return (
    <section style={{ background:'transparent', padding:'clamp(48px,6vw,80px) 0' }}>
      <div style={{ maxWidth:1280, margin:'0 auto', padding:'0 clamp(20px,5vw,48px)' }}>
        <div ref={ref} style={{ marginBottom:40, opacity:visible?1:0, transform:visible?'none':'translateY(18px)', transition:'opacity 480ms' }}>
          <span style={{ fontFamily:"'Anton', sans-serif", fontSize:10.5, color:color, textTransform:'uppercase' }}>WHAT WE DELIVER</span>
          <h2 style={{ fontFamily:"'Anton',sans-serif", fontSize:'clamp(28px,4vw,48px)', lineHeight:1.07, marginTop:8 }}>
            Your{' '}<span style={{ fontFamily:"'Instrument Serif',serif", fontStyle:'italic', fontWeight:400, color:color }}>Deliverables</span>
          </h2>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px,1fr))', gap:'clamp(12px,1.8vw,18px)' }}>
          {items.map((item, i) => (
            <div key={i} style={{ padding:'clamp(18px,2.2vw,26px)', background:'rgba(255,255,255,0.78)', border:'1px solid rgba(10,10,20,0.08)', borderRadius:16, opacity:visible?1:0, transform:visible?'none':'translateY(18px)', transition:`opacity 450ms ${i*45+80}ms, transform 450ms ${i*45+80}ms` }}>
              <div style={{ display:'flex', gap:12, alignItems:'flex-start' }}>
                <div style={{ width:20, height:20, borderRadius:6, background:`${color}12`, border:`1px solid ${color}28`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:2 }}>
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>
                <div>
                  <div style={{ fontSize:14.5, fontWeight:700, color:'var(--ink)', marginBottom:5, lineHeight:1.3 }}>{item.title}</div>
                  <div style={{ fontSize:13.5, lineHeight:1.7, color:'var(--ink-soft)' }}>{item.desc}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
*/
export function WhatWeDeliver() { return null; }
